import React, {useCallback, useState, useMemo} from 'react';

import {isEditableAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {isAnalysisTemplateGraphQL} from 'analysis-tool/AnalysisTypes';
import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import useCheckStepTypeEnabledForUser from 'pipelines/PipelineCapabilities';
import {getLatestStepVersion, getSourceStepTypes} from 'pipelines/StepConnectionUtils';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';
import StepTypeSelector from './StepTypeSelector';

/**
 * Step types offered in the "Add source step" sidebar in addition to the true source step types, which
 * require no required inputs or integrations.
 */
const PSEUDO_SOURCE_STEP_TYPES: ReadonlySet<PipelineStepType> = new Set([
  /**
   * Join is included as a "source step" since "+ Add data" is historically where we have exposed this option.
   *
   * When added from ADD_SOURCE_STEP, the sidebar advances to COMBINE_TABLES_SOURCE, so users can select input
   * tables without using the flowchart.
   */
  PipelineStepType.JOIN,
  /**
   * Union is included as a "source step" since "+ Add data" is historically where we have exposed this option.
   *
   * When added from ADD_SOURCE_STEP, the sidebar advances to COMBINE_TABLES_SOURCE, so users can select input
   * tables without using the flowchart.
   */
  PipelineStepType.UNION_DATASETS,
  /**
   * Look up is included as a "source step" since "+ Add data" is historically where we have exposed this option.
   *
   * When added from ADD_SOURCE_STEP, the sidebar advances to LOOK_UP_SOURCE, so users can select an input
   * table without using the flowchart.
   */
  PipelineStepType.LOOK_UP,
  /**
   * THIRD_PARTY steps are added via API integrations; while users cannot add THIRD_PARTY steps directly they
   * are included as a pseudo-source step, so that users can copy an auth token for the integration.
   *
   * Advances to THIRD_PARTY_IMPORT.
   */
  PipelineStepType.THIRD_PARTY,
]);

const HIDDEN_SOURCE_STEP_TYPES: ReadonlySet<PipelineStepType> = new Set([
  // Exposed via PipelineStepType.DATA_CATALOG_DATASET, when editing a template.
  PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
]);

/**
 * Add a new source step to the pipeline.
 *
 * This includes both true source steps and several `PSEUDO_SOURCE_STEP_TYPES` which are exposed through
 * "+ Add data" for lack of a better UI entry point.
 *
 * Some source step types progress to a dedicated sidebar, due to advanced or unusual configuration.
 */
export default function AddSourceStepRightSidebar({
  analysis: pipeline,
  onChangeSelectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.ADD_SOURCE_STEP>): JSX.Element {
  const [selectedStepType, setSelectedStepType] = useState<PipelineStepType | null>(null);

  const checkStepTypeEnabledForUser = useCheckStepTypeEnabledForUser(pipeline.id);
  const unfilteredSourceStepTypes: Set<PipelineStepType> = useMemo(() => new Set(getSourceStepTypes()), []);

  const filterStepTypes = useCallback(
    (stepType: PipelineStepType): boolean => {
      if (HIDDEN_SOURCE_STEP_TYPES.has(stepType)) {
        return false;
      }
      if (
        !(
          unfilteredSourceStepTypes.has(stepType) &&
          stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType))
        ) &&
        !PSEUDO_SOURCE_STEP_TYPES.has(stepType)
      ) {
        return false;
      }

      // Set `forCreate` to false for THIRD_PARTY since THIRD_PARTY steps aren't created directly by the user.
      const forCreate = stepType !== PipelineStepType.THIRD_PARTY;
      return checkStepTypeEnabledForUser(stepType, forCreate);
    },
    [checkStepTypeEnabledForUser, unfilteredSourceStepTypes]
  );

  // Add the new step type when Next is clicked
  const onNext = async (): Promise<void> => {
    if (!selectedStepType) {
      return;
    }
    switch (selectedStepType) {
      case PipelineStepType.THIRD_PARTY:
        onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.THIRD_PARTY_IMPORT});
        return;
      case PipelineStepType.JOIN:
      case PipelineStepType.UNION_DATASETS:
        onChangeSelectedRightSidebarItem({
          type: AnalysisRightSidebarItemType.COMBINE_TABLES_SOURCE,
          initialCombineMethod: selectedStepType,
        });
        return;
      case PipelineStepType.LOOK_UP:
        onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.LOOK_UP_SOURCE});
        return;
      case PipelineStepType.DATA_CATALOG_DATASET:
        // If the user selects DATA_CATALOG_DATASET when configuring a template, we redirect to
        // ADD_TEMPLATE_INPUT_SOURCE, so the user can configure either a variable or a static input table.
        if (isAnalysisTemplateGraphQL(pipeline) && isEditableAnalysisTemplatesEnabled()) {
          onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE});
          return;
        }
        break;
      default:
        break;
    }
    const {stepId} = await withLoadingScreen(
      addStepWithInferredEdges(pipeline, {stepType: selectedStepType})
    );
    await withLoadingScreen(refreshAnalysis(pipeline.id));
    onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
  };

  return (
    <RightSidebarSkeleton
      title="Add source step"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !selectedStepType,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddStepRightSidebar',
        element: 'root',
      }}
    >
      <StepTypeSelector
        selectedStepType={selectedStepType}
        onSelectStepType={setSelectedStepType}
        filterStepTypes={filterStepTypes}
      />
    </RightSidebarSkeleton>
  );
}
